import { z } from "zod"
import { LegacyCompatibleDiscountSchema } from "./discount"
import { ReceptionEvaluatedPartSchema } from "./evaluated-service-bike-part"
import { MAX_FILES_PER_JOB, StagedJobAttachmentSchema } from "./job"
import { ActiveSecondsSchema, InteractionEffortSchema } from "./job-timing"
import {
  CustomerInfoInReceptionSchema,
  ManualServiceActionInReceptionSchema,
  ServiceWashInReceptionSchema,
} from "./reception"
import { ServiceBikeType } from "./service-bike"

/**
 * Simplified bike schema for NewManualReception.
 * Does not require brake systems or gear systems.
 */
export const SimplifiedBikeSchema = z.object({
  id: z.string().optional(),
  bikeBrandId: z.string().uuid().nullable(),
  model: z.string().nullable(),
  color: z.string().nullable(),
  type: z.nativeEnum(ServiceBikeType),
  customBikeTypeId: z.string().uuid().nullable().optional(),
  isElectric: z.boolean().nullable(),
  /** ID of a previously stored bike to copy detailed config from (brakes, gears, dampers) */
  previousBikeId: z.string().uuid().optional(),
  /** The mechanic said this bike hasn't been here before, so it joins none of the customer's bikes. */
  isNewBike: z.boolean().optional(),
})

export type SimplifiedBike = z.infer<typeof SimplifiedBikeSchema>

/**
 * Schema for submitting a new manual reception.
 */
export const SubmitNewManualReceptionSchema = z.object({
  customer: CustomerInfoInReceptionSchema,
  agreedToJoinStoreClub: z.boolean(),
  agreedToContactInfoBeingStored: z.boolean(),
  customerBudget: z.number().nonnegative(),
  mechanicId: z.string().uuid(),
  assignedMechanicId: z.string().uuid().nullable(),
  bike: SimplifiedBikeSchema,
  wash: ServiceWashInReceptionSchema.nullable(),
  fees: z.array(z.string().uuid()),
  packages: z.array(z.string().uuid()),
  manualServiceActions: z.array(ManualServiceActionInReceptionSchema),
  evaluatedParts: z.array(ReceptionEvaluatedPartSchema).default([]),
  internalNote: z.string().nullable(),
  physicalTag: z.number().nullable(),
  labelIds: z.array(z.string().uuid()).default([]),
  leasingId: z.string().trim().max(200).nullable().optional(),
  // Discounts
  laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  packageDiscounts: z
    .record(z.string().uuid(), LegacyCompatibleDiscountSchema.nullable())
    .optional(),
  discountCodeId: z.string().uuid().optional(),
  // Active seconds the mechanic spent in the reception form
  activeSeconds: ActiveSecondsSchema.optional(),
  effort: InteractionEffortSchema.optional(),
  // Optional booking job ID - when provided, updates existing job instead of creating new
  bookingJobId: z.string().uuid().optional(),
  // Optional service event ID - when provided, links the job to this service event
  serviceEventId: z.string().uuid().optional(),
  // Optional price list ID - when provided, uses this price list instead of default
  priceListId: z.string().uuid().optional(),
  // Service day - the date when the bike was handed in for service
  serviceDay: z.coerce.date(),
  // Optional company linkage — picking a contact person determines both the
  // company and the avdelning (derived via CompanyContactPerson at read time).
  companyContactPersonId: z.string().uuid().nullable().optional(),
  reference: z.string().max(500).nullable().optional(),
  eServiceNumber: z.string().trim().max(100).nullable().optional(),
  // Files already uploaded to the staging endpoint; backend turns these into
  // JobAttachment rows once the job is created.
  attachments: z
    .array(StagedJobAttachmentSchema)
    .max(MAX_FILES_PER_JOB)
    .optional(),
})

export type SubmitNewManualReception = z.infer<
  typeof SubmitNewManualReceptionSchema
>

/**
 * Per-bike entry for multi-bike reception.
 * Contains all fields that are specific to a single bike/job.
 */
export const BikeEntrySchema = z.object({
  bike: SimplifiedBikeSchema,
  wash: ServiceWashInReceptionSchema.nullable(),
  fees: z.array(z.string().uuid()),
  packages: z.array(z.string().uuid()),
  manualServiceActions: z.array(ManualServiceActionInReceptionSchema),
  evaluatedParts: z.array(ReceptionEvaluatedPartSchema).default([]),
  internalNote: z.string().nullable(),
  physicalTag: z.number().nullable(),
  labelIds: z.array(z.string().uuid()).default([]),
  leasingId: z.string().trim().max(200).nullable().optional(),
  customerBudget: z.number().nonnegative(),
  assignedMechanicId: z.string().uuid().nullable(),
  // Discounts
  laborDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  productsDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable().optional(),
  packageDiscounts: z
    .record(z.string().uuid(), LegacyCompatibleDiscountSchema.nullable())
    .optional(),
  discountCodeId: z.string().uuid().optional(),
  // Files already uploaded to the staging endpoint for this bike's job.
  attachments: z
    .array(StagedJobAttachmentSchema)
    .max(MAX_FILES_PER_JOB)
    .optional(),
})

export type BikeEntry = z.infer<typeof BikeEntrySchema>

/**
 * Schema for submitting multiple bikes in a single reception.
 * Shared fields (customer, mechanic, service day) + array of per-bike entries.
 */
export const SubmitNewManualReceptionMultipleBikesSchema = z.object({
  customer: CustomerInfoInReceptionSchema,
  agreedToJoinStoreClub: z.boolean(),
  agreedToContactInfoBeingStored: z.boolean(),
  mechanicId: z.string().uuid(),
  serviceDay: z.coerce.date(),
  priceListId: z.string().uuid().optional(),
  serviceEventId: z.string().uuid().optional(),
  bikes: z.array(BikeEntrySchema).min(1).max(10),
  // Active seconds for the whole form; the API splits them across the bikes
  activeSeconds: ActiveSecondsSchema.optional(),
  // Split across the bikes the same way
  effort: InteractionEffortSchema.optional(),
  // Optional company linkage (applied to all created jobs). Picking a contact
  // person determines both the company and the avdelning.
  companyContactPersonId: z.string().uuid().nullable().optional(),
  reference: z.string().max(500).nullable().optional(),
  eServiceNumber: z.string().trim().max(100).nullable().optional(),
})

export type SubmitNewManualReceptionMultipleBikes = z.infer<
  typeof SubmitNewManualReceptionMultipleBikesSchema
>

/**
 * Schema for submitting a quick reception (snabbmottagning).
 * Used for fast processing of existing bookings at mobile events.
 * The backend resolves customer, bike, and mechanic data from the existing job.
 */
export const SubmitQuickReceptionSchema = z.object({
  bookingJobId: z.string().uuid(),
  internalNote: z.string().nullable(),
  physicalTag: z.number().nullable(),
})

export type SubmitQuickReception = z.infer<typeof SubmitQuickReceptionSchema>

/**
 * Detect-then-validate schema for the union endpoint.
 * Checks for 'bikes' field to determine which schema to validate against.
 * Produces clean error messages (no confusing z.union() combined errors).
 */
export const NewManualReceptionUnionSchema = z
  .record(z.string(), z.unknown())
  .transform(
    (
      val,
      ctx,
    ): SubmitNewManualReception | SubmitNewManualReceptionMultipleBikes => {
      if ("bikes" in val && Array.isArray(val.bikes)) {
        const result =
          SubmitNewManualReceptionMultipleBikesSchema.safeParse(val)
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue(issue)
          }
          return z.NEVER
        }
        return result.data
      }
      const result = SubmitNewManualReceptionSchema.safeParse(val)
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue(issue)
        }
        return z.NEVER
      }
      return result.data
    },
  )
